import React, { useEffect, useRef, useState } from "react";
import { LockKeyhole, LogOut, UserCircle2, X } from "lucide-react";
import { authFetch, getStoredAccessToken } from "../utils/auth";

const UserProfileMenu = ({ onLogout, className = "", variant = "default", sentiment, prevSentiment }) => {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const username = localStorage.getItem("username") || "User";
  const role = localStorage.getItem("role") || "user";

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccessMessage("");
    setSaving(false);
  };

  const submitPasswordChange = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please fill all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirm password must match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (!getStoredAccessToken()) {
      setError("Login token missing. Please sign in again.");
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch("/api/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || "Unable to change password.");
      }

      setSuccessMessage(payload?.message || "Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.message || "Unable to change password.");
    } finally {
      setSaving(false);
    }
  };

  const formatRoleLabel = (r) => {
    const norm = String(r || "").toLowerCase().replace("_", "-");
    if (norm === "support-agent" || norm === "support" || norm === "agent" || norm === "tse") {
      return "Support Engineer";
    }
    if (norm === "user") return "User";
    if (norm === "admin") return "Admin";
    return r;
  };

  return (
    <>
      <div ref={wrapperRef} className={`relative ${className}`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className={`w-10 h-10 rounded-full text-white transition-colors shadow-md flex items-center justify-center ${variant === "enterprise" ? "bg-[#800020] hover:bg-[#600018]" : "bg-[#06b6d4] hover:bg-cyan-600"}`}
            title={`${username} (${formatRoleLabel(role)})`}
          >
            <span className="font-semibold text-sm">
              {username.charAt(0).toUpperCase()}
            </span>
          </button>
        </div>

        {open && (
          <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg py-2 z-50">
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="text-sm font-semibold text-[#253439] truncate">{username}</div>
              <div className="text-xs text-gray-500">{formatRoleLabel(role)}</div>
            </div>

            {/* ONE number. "Previously / Currently" showed two readings of a
                running average that moves by a fraction a turn, so they were
                almost always identical - two lines saying the same thing, and
                inviting a comparison the numbers could not support. The
                current reading is the one that matters. */}
            {(typeof sentiment === 'number' || typeof prevSentiment === 'number') && (
              <div className="px-4 pt-2 pb-2 text-xs text-[#253439]">
                <div className="text-gray-500">Sentiment meter</div>
                <div className="mt-0.5 font-semibold text-gray-900 text-sm">
                  {typeof sentiment === 'number' ? sentiment.toFixed(1) : sentiment}/10
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setShowPasswordModal(true);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-[#253439] hover:bg-cyan-50 flex items-center gap-2"
            >
              <LockKeyhole size={16} />
              Change Password
            </button>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onLogout?.();
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        )}
      </div>

      {showPasswordModal && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#253439]">
                <UserCircle2 size={18} />
                <h3 className="font-semibold">Change Password</h3>
              </div>
              <button
                type="button"
                onClick={closePasswordModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={submitPasswordChange} className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}
              {successMessage && <div className="text-sm text-green-600">{successMessage}</div>}

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#06b6d4] text-white rounded-lg text-sm hover:bg-cyan-700 disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UserProfileMenu;
